import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Search, Trash2 } from "lucide-react";
import {
  TenantAttributeTypeI18N,
  type TenantAttributeSpec,
} from "@the-real-insight/tri-model";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import type { DinTree } from "../../../administration/din276/din276Tree";
import AttributeDialog from "./AttributeDialog";
import {
  EMPTY_ATTRIBUTE_FILTER,
  type AttributeFilter,
  type AttributeRow,
  type TenantAttribute,
  attributeRows,
  countFields,
  describeFields,
  sharedName,
} from "./dataModel";

/**
 * "Attribute": every customer attribute, one row per class, filtered by class name, class code and
 * attribute name. Rows are removed one by one, as a selection or as the whole filter result; when
 * the rows left are one attribute (one name), they can be changed as a whole.
 */

interface Properties {
  tree: DinTree | null;
  attributes: TenantAttribute[];
  canWrite: boolean;
  onUpdate: (ids: string[], spec: TenantAttributeSpec) => Promise<void>;
  onDelete: (ids: string[]) => Promise<number>;
}

export default function AttributesTab({
  tree,
  attributes,
  canWrite,
  onUpdate,
  onDelete,
}: Properties) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const fieldId = useId();

  const [filter, setFilter] = useState<AttributeFilter>(EMPTY_ATTRIBUTE_FILTER);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** The rows being changed: one, from its own pencil, or the whole filtered set of one name. */
  const [editing, setEditing] = useState<AttributeRow[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);

  const rows = useMemo(
    () => attributeRows(attributes, tree, filter),
    [attributes, tree, filter],
  );
  const name = sharedName(rows);

  // A selection only ever refers to rows the reader can see.
  useEffect(() => {
    const visible = new Set(rows.map((row) => row._id));
    setSelected(
      (current) => new Set(Array.from(current).filter((id) => visible.has(id))),
    );
  }, [rows]);

  const change = (patch: Partial<AttributeFilter>) => {
    setFilter((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const toggle = (id: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const remove = async () => {
    if (!confirmDelete) return;
    const ids = confirmDelete;
    setConfirmDelete(null);
    try {
      const deleted = await onDelete(ids);
      toast.success(
        t("operatorObligationsPanel.dataModel.attributes.deleted", {
          count: deleted,
        }),
      );
    } catch (error: any) {
      toast.error(error?.message ?? t("global.generalError"));
    }
  };

  const update = async (spec: TenantAttributeSpec) => {
    if (!editing) return;
    await onUpdate(
      editing.map((row) => row._id),
      spec,
    );
    toast.success(
      t("operatorObligationsPanel.dataModel.attributes.updated", {
        count: editing.length,
        name: spec.name,
      }),
    );
    setEditing(null);
  };

  // Stable while the dialog is open: a new object on every render would reset what is being typed.
  const initial = useMemo<TenantAttributeSpec | undefined>(() => {
    const first = editing?.[0];
    return first
      ? {
          name: first.name,
          valueType: first.valueType,
          unit: first.unit ?? undefined,
          allowedValues: first.allowedValues,
          fields: first.fields,
        }
      : undefined;
  }, [editing]);

  const allSelected =
    rows.length > 0 && rows.every((row) => selected.has(row._id));

  const columns: ColumnDef<AttributeRow>[] = [
    ...(canWrite
      ? [
          {
            id: "select",
            size: 40,
            header: () => (
              <Checkbox
                checked={allSelected}
                aria-label={t(
                  "operatorObligationsPanel.dataModel.attributes.selectAll",
                )}
                onCheckedChange={(checked) =>
                  setSelected(
                    checked === true
                      ? new Set(rows.map((row) => row._id))
                      : new Set(),
                  )
                }
              />
            ),
            cell: ({ row }: { row: { original: AttributeRow } }) => (
              <Checkbox
                checked={selected.has(row.original._id)}
                aria-label={t(
                  "operatorObligationsPanel.dataModel.attributes.select",
                  {
                    name: row.original.name,
                    code: row.original.classCode,
                  },
                )}
                onCheckedChange={(checked) =>
                  toggle(row.original._id, checked === true)
                }
              />
            ),
          } as ColumnDef<AttributeRow>,
        ]
      : []),
    {
      id: "classCode",
      header: t("operatorObligationsPanel.dataModel.classes.code"),
      accessorFn: (row) => row.classCode,
      size: 100,
    },
    {
      id: "className",
      header: t("operatorObligationsPanel.dataModel.attributes.className"),
      accessorFn: (row) => row.className,
      size: 240,
    },
    {
      id: "name",
      header: t("operatorObligationsPanel.dataModel.attribute.name"),
      accessorFn: (row) => row.name,
      size: 200,
    },
    {
      id: "type",
      header: t("operatorObligationsPanel.dataModel.attribute.type"),
      accessorFn: (row) => {
        const type =
          TenantAttributeTypeI18N[row.valueType]?.[lang] ?? row.valueType;
        return row.fields?.length
          ? t("operatorObligationsPanel.dataModel.attributes.structureType", {
              type,
              count: countFields(row.fields),
            })
          : type;
      },
      size: 120,
    },
    {
      id: "unit",
      header: t("operatorObligationsPanel.dataModel.attribute.unit"),
      accessorFn: (row) => row.unit ?? "",
      size: 90,
    },
    {
      id: "values",
      header: t("operatorObligationsPanel.dataModel.attributes.valuesOrFields"),
      accessorFn: (row) =>
        row.fields?.length
          ? describeFields(row.fields)
          : row.allowedValues.join(" · "),
      size: 220,
      cell: ({ row }) => {
        // A structure shows its shape here; an enumeration its values.
        const text = row.original.fields?.length
          ? describeFields(row.original.fields)
          : row.original.allowedValues.join(" · ");
        return (
          <span className="block max-w-[220px] truncate" title={text}>
            {text}
          </span>
        );
      },
    },
    ...(canWrite
      ? [
          {
            id: "actions",
            size: 90,
            header: "",
            cell: ({ row }: { row: { original: AttributeRow } }) => (
              <div className="flex">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  aria-label={t(
                    "operatorObligationsPanel.dataModel.attributes.editOne",
                    {
                      name: row.original.name,
                      code: row.original.classCode,
                    },
                  )}
                  onClick={() => setEditing([row.original])}
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  aria-label={t(
                    "operatorObligationsPanel.dataModel.attributes.deleteOne",
                    {
                      name: row.original.name,
                      code: row.original.classCode,
                    },
                  )}
                  onClick={() => setConfirmDelete([row.original._id])}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ),
          } as ColumnDef<AttributeRow>,
        ]
      : []),
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[240px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-class`}>
            {t("operatorObligationsPanel.dataModel.attributes.className")}
          </Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id={`${fieldId}-class`}
              type="search"
              className="pl-9"
              value={filter.className}
              onChange={(event) => change({ className: event.target.value })}
            />
          </div>
        </div>
        <div className="flex w-[140px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-code`}>
            {t("operatorObligationsPanel.dataModel.classes.codeFilter")}
          </Label>
          <Input
            id={`${fieldId}-code`}
            type="search"
            placeholder="440"
            value={filter.code}
            onChange={(event) => change({ code: event.target.value })}
          />
        </div>
        <div className="flex w-[240px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-name`}>
            {t("operatorObligationsPanel.dataModel.attribute.name")}
          </Label>
          <Input
            id={`${fieldId}-name`}
            type="search"
            value={filter.name}
            onChange={(event) => change({ name: event.target.value })}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {t("operatorObligationsPanel.dataModel.attributes.count", {
            count: rows.length,
          })}
        </span>
        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={!name}
              title={
                !name
                  ? t("operatorObligationsPanel.dataModel.attributes.editHint")
                  : undefined
              }
              onClick={() => setEditing(rows)}
            >
              <Pencil size={14} aria-hidden />
              {name
                ? t("operatorObligationsPanel.dataModel.attributes.editAll", {
                    name,
                    count: rows.length,
                  })
                : t(
                    "operatorObligationsPanel.dataModel.attributes.editDisabled",
                  )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={selected.size === 0}
              onClick={() => setConfirmDelete(Array.from(selected))}
            >
              <Trash2 size={14} aria-hidden />
              {t(
                "operatorObligationsPanel.dataModel.attributes.deleteSelected",
                { count: selected.size },
              )}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="gap-1.5"
              disabled={rows.length === 0}
              onClick={() => setConfirmDelete(rows.map((row) => row._id))}
            >
              <Trash2 size={14} aria-hidden />
              {t("operatorObligationsPanel.dataModel.attributes.deleteAll", {
                count: rows.length,
              })}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="min-w-0 overflow-x-auto">
        <DataTable
          columns={columns}
          data={rows}
          pageSize={15}
          page={page}
          onPageChange={setPage}
          emptyMessage={
            attributes.length === 0
              ? t("operatorObligationsPanel.dataModel.attributes.noneYet")
              : t("operatorObligationsPanel.dataModel.attributes.none")
          }
          previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
          nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
        />
      </div>

      <AttributeDialog
        open={!!editing}
        title={t("operatorObligationsPanel.dataModel.attributes.editTitle", {
          name: initial?.name ?? "",
        })}
        description={
          editing?.length === 1
            ? t(
                "operatorObligationsPanel.dataModel.attributes.editOneDescription",
                { code: editing[0].classCode, className: editing[0].className },
              )
            : t(
                "operatorObligationsPanel.dataModel.attributes.editDescription",
                { count: editing?.length ?? 0 },
              )
        }
        confirmLabel={t(
          "operatorObligationsPanel.dataModel.attributes.editConfirm",
        )}
        initial={initial}
        onDismiss={() => setEditing(null)}
        onConfirm={update}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("operatorObligationsPanel.dataModel.attributes.deleteTitle", {
                count: confirmDelete?.length ?? 0,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "operatorObligationsPanel.dataModel.attributes.deleteDescription",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={remove}
            >
              {t("operatorObligationsPanel.dataModel.attributes.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
